import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, IconButton } from '@mui/material';
import qrImage from '../../../assets/qrCode.png';
import UnableToDeliverModal from './unable-to-deliver-modal.tsx';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useLocation, useNavigate } from 'react-router-dom';
import { editOrders, editItems } from './action.ts';
import { toast } from 'react-toastify';

interface QRCodePaymentScreenProps {
  onAmountReceived: (upiAmount: number, cashAmount: number) => void;
  onClose: () => void;
}
interface OrderDetails {
  id: string;
  order_id: string;
  name: string;
  address: string;
  pincode: string;
  order_type: string;
  payment_mode: string;
  amount: number;
  [key: string]: any;
}
const QRCodePaymentScreen: React.FC<QRCodePaymentScreenProps> = ({
  onAmountReceived,
  onClose,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    amount = 0,
    cashPaid = 0,
    orderId,
    warehouseId,
    vehicleId,
    items,
    isPartialDelivery = false,
  } = location.state || {};

  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState<'unable' | 'partial' | null>(null);
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const order = location.state as OrderDetails | null;
  const [urlParams, setUrlParams] = useState({
    token: '',
    userId: '',
    vehicleId: '',
  });

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    setUrlParams({
      token: queryParams.get('token') || '',
      userId: queryParams.get('user_id') || '',
      vehicleId: queryParams.get('vehicle_id') || '',
    });
  }, []);

  const handleUnableToDeliverClick = () => {
    setModalType('unable');
    setModalOpen(true);
  };

  const handlePartialDeliveryClick = () => {
    setModalType('partial');
    setModalOpen(true);
  };

  const handleAmountReceivedClick = async () => {
    const status = isPartialDelivery ? 'Partially Delivered' : 'Delivered';
    try {
      setIsLoading(true);
      await editOrdersAPI(status, cashPaid, amount, '');
      if (items && items.length > 0) {
        await updateItemsStatusForDelivered();
      }

      onAmountReceived?.(amount, cashPaid);
      navigate(
        `/wv/orders?vehicle_id=${urlParams.vehicleId}&user_id=${urlParams.userId}&token=${urlParams.token}`
      );
    } catch (error) {
      toast.error('Failed to update order status');
    } finally {
      setIsLoading(false);
    }
  };

  const updateItemsStatusForDelivered = async () => {
    try {
      const checkedItems = items.filter((item) => item.checked);

      const updatePromises = checkedItems.map(
        (item) =>
          new Promise<void>((resolve, reject) => {
            editItems(
              item.id,
              'Delivered',
              () => resolve(),
              (error) => reject(error)
            );
          })
      );

      await Promise.all(updatePromises);
      toast.success('Selected items marked as delivered');
    } catch (error) {
      toast.error('Some items failed to update');
    }
  };

  const editOrdersAPI = async (
    status: string,
    amount_cash: number = 0,
    amount_upi: number = 0,
    remarks: string = ''
  ) => {
    return new Promise<void>((resolve, reject) => {
      editOrders(
        order?.order?.order?.id,
        warehouseId,
        vehicleId,
        status,
        amount_upi,
        amount_cash,
        remarks,
        () => {
          toast.success('Order status updated successfully');
          resolve();
        },
        (error) => {
          toast.error(error || 'Failed to update order status');
          reject(error);
        }
      );
    });
  };

  const updateItemsStatusForAttempted = async (status: string) => {
    try {
      const updatePromises = items.map(
        (item) =>
          new Promise<void>((resolve, reject) => {
            editItems(
              item.id,
              status,
              () => resolve(),
              (error) => reject(error)
            );
          })
      );

      await Promise.all(updatePromises);
      toast.success("All items updated to " + status);
    } catch (error) {
      toast.error("Some items failed to update");
    }
  };

  const handleUnableToDeliverSubmit = async (selectedReason: string) => {
    try {
      setIsLoading(true);
      const status = selectedReason === "Customer Cancelled" ? "Cancelled" : "Attempted";

      if (modalType === "unable") {
        await editOrdersAPI(status, 0, 0, selectedReason);
        await updateItemsStatusForAttempted("Attempted");
      } else {
        await editOrdersAPI("Partially Delivered", 0, 0, selectedReason);
        await updateItemsStatusForAttempted("Attempted");
      }

      setModalOpen(false);
      navigate(
        `/wv/orders?vehicle_id=${urlParams.vehicleId}&user_id=${urlParams.userId}&token=${urlParams.token}`
      );
    } catch (error) {
      toast.error("Failed to update order status");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <Box p={3} textAlign="center">
      <Typography variant="h6" fontWeight="bold" gutterBottom>
        <IconButton
          edge="start"
          color="inherit"
          aria-label="back"
          onClick={handleBack}
          size="large"
          sx={{
            '& .MuiSvgIcon-root': {
              fontSize: '2rem',
              fontWeight: 'bold',
            },
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        Amount Payable: ₹ {amount?.toFixed(2)}
        {cashPaid > 0 && (
          <Typography variant="body2" color="text.secondary">
            (After cash payment of ₹{cashPaid.toFixed(2)})
          </Typography>
        )}
      </Typography>

      <Typography variant="subtitle1" gutterBottom>
        Scan QR Code
      </Typography>
      <Box my={2}>
        <img src={qrImage} alt="QR Code" style={{ width: 200, height: 200 }} />
      </Box>
      
      {!isPartialDelivery && (
        <Button
          variant="contained"
          color="success"
          onClick={handleAmountReceivedClick}
          sx={{ mt: 2, mr: 2 }}
          disabled={isLoading}
        >
          {isLoading ? 'Processing...' : 'Amount Received'}
        </Button>
      )}
      
      {isPartialDelivery && (
        <Button
          variant="contained"
          color="primary"
          onClick={handlePartialDeliveryClick}
          sx={{ mt: 2, mr: 2 }}
          disabled={isLoading}
        >
          {isLoading ? 'Processing...' : 'Partial Delivery'}
        </Button>
      )}
      
      <Button
        variant="outlined"
        color="error"
        onClick={handleUnableToDeliverClick}
        sx={{ mt: 2 }}
        disabled={isLoading}
      >
        {isLoading ? 'Processing...' : 'Unable to Deliver'}
      </Button>

      <UnableToDeliverModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleUnableToDeliverSubmit}
        reason={reason}
        setReason={setReason}
        isLoading={isLoading}
        type={modalType}
      />
    </Box>
  );
};

export default QRCodePaymentScreen;